/**
 * Area `recording` of the console gateway — plan-003 RC-46 (design §8.8, AC-33).
 *
 * Playing a recording is not a blob URL handed to the console. It is one audited act per play:
 * `POST /v1/conversations/{conversation_id}/recording/access` reads through `recording_visible` (a
 * Member gets nothing, at the data layer), writes the customer's `recording.played` audit entry, and
 * only then returns a five-minute read URL scoped to that one file. So the History panel asks for the
 * URL when somebody presses play or download — never when the panel opens, which would log a play
 * nobody made.
 *
 * The register is EMPTY: the operation is served.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap } from "./slice-context.js";

/** One granted play: the URL (never logged, never stored), when it stops working, and what it holds. */
export interface RecordingAccessView {
  readonly url: string;
  /** ISO-8601; the URL is refused after this. */
  readonly expiresAt: string;
  readonly durationSeconds: number;
  readonly codec: string;
  /**
   * The name the URL is signed to download as (review 122, fix round R: the storage service answers
   * it with `Content-Disposition: attachment; filename="…"`), so opening the URL saves a named file
   * with no CORS. `null` for a URL that carries no such header (the sandbox's).
   */
  readonly fileName: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * Grants one play of a conversation's recording (design §8.8): the audit entry is written before
     * the URL exists. Raises the API's 403 for a reader the recording is not visible to.
     */
    accessRecording(conversationId: string): Promise<RecordingAccessView>;
  }
}

export const recordingGap: readonly GatewayGap[] = [];

export function recordingAccessOf(body: unknown): RecordingAccessView {
  const data = readRecord(readRecord(body, "accessConversationRecording")["data"], "accessConversationRecording.data");
  return {
    url: readText(data["url"], "accessConversationRecording.data.url"),
    expiresAt: readText(data["expires_at"], "accessConversationRecording.data.expires_at"),
    durationSeconds: Math.round(readWholeNumber(data["duration_ms"], "accessConversationRecording.data.duration_ms") / 1000),
    codec: readText(data["codec"], "accessConversationRecording.data.codec"),
    fileName: readText(data["file_name"], "accessConversationRecording.data.file_name"),
  };
}

export function recordingApiSlice(client: WeTalkApiClient): Pick<ConsoleGateway, "accessRecording"> {
  return {
    async accessRecording(conversationId: string): Promise<RecordingAccessView> {
      return recordingAccessOf(
        assertOk(await client.accessConversationRecording({ path: { conversation_id: conversationId } })),
      );
    },
  };
}
